import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trainingNodesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  GraduationCap, 
  Play,
  CheckCircle,
  Clock,
  ExternalLink,
  Search,
  BookOpen,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const JOB_ROLES = [
  "Clin Ops",
  "Reg Ops", 
  "Quality Ops",
  "Recr Ops",
  "eSource Ops",
  "Data Ops",
  "Budget Ops"
];

const TrainingList = () => {
  const { user, canManage } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [assignedCurricula, setAssignedCurricula] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [filterType, setFilterType] = useState('all'); // all, mandatory, reference, completed, pending
  const [hasAssignments, setHasAssignments] = useState(true);

  const categories = [
    'Mandatory',
    'Study Workflow',
    'Study Selection',
    'Study Startup',
    'Study Conduct',
    'Study Closeout',
    'Systems',
  ];

  useEffect(() => {
    loadData();
  }, [canManage]);

  const loadData = async () => {
    try {
      if (canManage) {
        // Admin/Manager/Lead can see all trainings
        const [trainingsRes, completionsRes] = await Promise.all([
          trainingNodesAPI.getAll(),
          trainingNodesAPI.getMyCompletions()
        ]);
        setTrainings(trainingsRes.data);
        setCompletions(completionsRes.data);
        setHasAssignments(true);
      } else {
        // Employees only see assigned trainings
        const assignedRes = await trainingNodesAPI.getMyAssigned();
        setTrainings(assignedRes.data.trainings || []);
        setAssignedCurricula(assignedRes.data.curricula || []);
        setHasAssignments(assignedRes.data.trainings?.length > 0);
        
        // Build completions from the response
        const completedTrainings = assignedRes.data.trainings
          .filter(t => t.is_completed)
          .map(t => ({ node_id: t.id }));
        setCompletions(completedTrainings);
      }
    } catch (error) {
      console.error('Failed to load trainings:', error);
      toast.error('Failed to load trainings');
    } finally {
      setLoading(false);
    }
  };

  const isCompleted = (nodeId) => {
    return completions.some(c => c.node_id === nodeId);
  };

  const handleMarkComplete = async (nodeId) => {
    try {
      await trainingNodesAPI.markComplete(nodeId);
      toast.success('Training marked as complete');
      loadData();
    } catch (error) {
      toast.error('Failed to mark training complete');
    }
  };

  const getRequirementForRole = (training, role) => {
    if (!training.role_requirements) return '';
    return training.role_requirements[role] || '';
  };

  const getUserJobRole = () => {
    return user?.job_role || 'Clin Ops';
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch = training.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         training.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || training.category === selectedCategory;
    
    let matchesRole = true;
    if (selectedRole !== 'all') {
      const req = getRequirementForRole(training, selectedRole);
      matchesRole = req === 'M' || req === 'R';
    }

    let matchesFilter = true;
    const userRole = getUserJobRole();
    const requirement = getRequirementForRole(training, userRole);
    const completed = isCompleted(training.id);
    
    if (filterType === 'mandatory') {
      matchesFilter = requirement === 'M';
    } else if (filterType === 'reference') {
      matchesFilter = requirement === 'R';
    } else if (filterType === 'completed') {
      matchesFilter = completed;
    } else if (filterType === 'pending') {
      matchesFilter = !completed;
    }

    return matchesSearch && matchesCategory && matchesRole && matchesFilter;
  });

  // Group trainings by category
  const groupedTrainings = filteredTrainings.reduce((acc, training) => {
    const category = training.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(training);
    return acc;
  }, {});

  // Sort categories
  const sortedCategories = Object.keys(groupedTrainings).sort((a, b) => {
    const order = ['Mandatory', 'Study Workflow', 'Study Selection', 'Study Startup', 'Study Conduct', 'Study Closeout', 'Systems'];
    return order.indexOf(a) - order.indexOf(b);
  });

  const getLinkIcon = (linkType) => {
    switch(linkType) {
      case 'loom': return '🎥';
      case 'youtube': return '📺';
      case 'web': return '🌐';
      case 'document': return '📄';
      default: return '🔗';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const userRole = getUserJobRole();
  const mandatoryCount = trainings.filter(t => getRequirementForRole(t, userRole) === 'M').length;
  const completedMandatory = trainings.filter(t => getRequirementForRole(t, userRole) === 'M' && isCompleted(t.id)).length;
  const totalCompleted = trainings.filter(t => isCompleted(t.id)).length;

  // Show message if employee has no assignments
  if (!canManage && !hasAssignments) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="training-list-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            My Trainings
          </h1>
          <p className="text-slate-600 mt-1">
            Complete your assigned training modules
          </p>
        </div>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>No trainings assigned yet.</strong> Your admin or lead will assign a curriculum to you based on your role. 
            Please contact your supervisor if you believe you should have training assignments.
          </AlertDescription>
        </Alert>

        {assignedCurricula.length > 0 && (
          <Card className="border-slate-200 shadow-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Your Assigned Curricula
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assignedCurricula.map(curriculum => (
                <div key={curriculum.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-2">
                  <div>
                    <p className="font-medium">{curriculum.title}</p>
                    <p className="text-sm text-slate-500">{curriculum.job_role}</p>
                  </div>
                  <Badge className={curriculum.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                    {curriculum.status === 'completed' ? 'Completed' : 'In Progress'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            {canManage ? 'Training Library' : 'My Trainings'}
          </h1>
          <p className="text-slate-600 mt-1">
            {canManage 
              ? 'View and manage all training modules' 
              : 'Complete your assigned training modules'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-500">Your Progress</p>
          <p className="text-2xl font-bold text-blue-600">
            {totalCompleted}/{trainings.length} <span className="text-sm font-normal text-slate-500">completed</span>
          </p>
          {mandatoryCount > 0 && (
            <p className="text-sm text-slate-500">
              {completedMandatory}/{mandatoryCount} mandatory
            </p>
          )}
        </div>
      </div>

      {/* Assigned Curricula Summary (for employees) */}
      {!canManage && assignedCurricula.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50 shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Briefcase className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-indigo-900">
                  Assigned Curricula: {assignedCurricula.map(c => c.title).join(', ')}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Role: {user?.job_role || 'Not set'} • {trainings.length} trainings assigned
                </p>
              </div>
              <Link to="/assignments">
                <Button variant="outline" size="sm" className="text-blue-600 border-blue-200">
                  View Details
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-200 shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search trainings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="training-search-input"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canManage && (
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-[150px]" data-testid="role-filter">
                  <SelectValue placeholder="Job Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {JOB_ROLES.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="mandatory">Mandatory</SelectItem>
                <SelectItem value="reference">Reference</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Training List by Category */}
      <div className="space-y-6">
        {sortedCategories.length === 0 ? (
          <Card className="border-slate-200 shadow-card">
            <CardContent className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No trainings match your filters</p>
            </CardContent>
          </Card>
        ) : (
          sortedCategories.map(category => (
            <Card key={category} className="border-slate-200 shadow-card">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  {category}
                  <Badge variant="outline" className="ml-2">
                    {groupedTrainings[category].length} trainings
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {groupedTrainings[category].map((training) => {
                    const completed = isCompleted(training.id);
                    const requirement = getRequirementForRole(training, userRole);
                    
                    return (
                      <div 
                        key={training.id}
                        className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors
                          ${completed ? 'bg-emerald-50/50' : ''}
                        `}
                        data-testid={`training-item-${training.id}`}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                            ${completed ? 'bg-emerald-100' : 'bg-blue-100'}
                          `}>
                            {completed ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <span className="text-lg">{getLinkIcon(training.link_type)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-slate-900 truncate">{training.label}</h3>
                              {requirement === 'M' && (
                                <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                              )}
                              {requirement === 'R' && (
                                <Badge variant="outline" className="text-slate-500 text-xs">Reference</Badge>
                              )}
                            </div>
                            {training.description && (
                              <p className="text-sm text-slate-500 truncate mt-0.5">{training.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          {training.link && (
                            <a href={training.link} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" size="sm" data-testid={`open-training-${training.id}`}>
                                <Play className="w-4 h-4 mr-1" />
                                Open
                                <ExternalLink className="w-3 h-3 ml-1" />
                              </Button>
                            </a>
                          )}
                          {!completed && (
                            <Button 
                              size="sm"
                              onClick={() => handleMarkComplete(training.id)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`complete-training-${training.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          {completed && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Done
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default TrainingList;
